import { Fragment, useState } from "react"
import {
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowUpRightFromSquare,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons"
import Loader from "../../../components/Loader"
import ApiError from "../../../components/ApiError"
import Pagination from "../../../components/Pagination"
import { formatISODate } from "../../../utils/datetime"
import StatusHint from "./StatusHint"
import {
  STAGE_BUCKETS,
  centerStatusOptions,
  unifiedStageOptions,
  toneStyles,
  toneDot,
  getUnifiedStage,
  getStageBucket,
} from "../statusConfig"

const centerStatusMeta = Object.fromEntries(
  centerStatusOptions.map((s) => [s.value, s])
)

const MiniStageBar = ({ cases }) => {
  const total = cases.length
  if (!total) return <div className="h-3 w-full rounded-full bg-gray-100" />

  const counts = STAGE_BUCKETS.map((bucket) => ({
    ...bucket,
    count: cases.filter((c) => bucket.test(getUnifiedStage(c))).length,
  })).filter((b) => b.count > 0)

  // The hover card sits outside the bar's overflow-hidden so it isn't
  // clipped; it doubles as the bar's color legend.
  return (
    <div className="group relative w-full">
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-gray-100">
        {counts.map((b) => (
          <span
            key={b.key}
            className={toneDot[b.tone]}
            style={{ width: `${(b.count / total) * 100}%` }}
          />
        ))}
      </div>
      <div className="absolute bottom-full left-0 z-10 mb-2 hidden flex-col gap-1 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-md group-hover:flex">
        {counts.map((b) => (
          <div
            key={b.key}
            className="flex items-center gap-1.5 text-xs text-gray-700"
          >
            <span
              className={`h-2.5 w-2.5 shrink-0 rounded-sm ${toneDot[b.tone]}`}
            />
            {b.label} ({b.count})
          </div>
        ))}
      </div>
    </div>
  )
}

const CenterStatusPill = ({ center, studyLimit }) => {
  const meta = centerStatusMeta[center.Status]
  if (!meta) return null
  return (
    <StatusHint align="right" text={meta.describe({ studyLimit, center })}>
      <span
        className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-medium ${
          meta.tone === "amber"
            ? "border-amber-300 bg-amber-50 text-amber-800"
            : meta.tone === "emerald"
              ? "border-emerald-300 bg-emerald-100 text-emerald-800"
              : "border-sky-200 bg-sky-50 text-secondary_dark"
        }`}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${toneDot[meta.tone]}`} />
        {meta.label}
      </span>
    </StatusHint>
  )
}

// Current stage of a case: the satisfaction deal's stage takes priority
// (it's always further along once it exists), else the case deal's
// stage, else "no deal" — same priority getUnifiedStage already encodes.
// Links to the deal that owns the stage.
const StageChip = ({ study }) => {
  const n = getUnifiedStage(study)
  if (n === null || n === undefined) {
    return <span className="text-sm text-gray-400">No deal</span>
  }

  // Colored by the stage's bucket, not its own status tone, so the chip
  // matches the progress bar and its hover breakdown.
  const stage = unifiedStageOptions.find((s) => s.n === n)
  const { tone } = getStageBucket(n)
  // Links come from the API (null when there's no deal or no portal set).
  const dealUrl = study.CustomerSatisfactionStatus
    ? study.CustomerSatisfactionDealUrl
    : study.TrackingDealUrl

  const chipClassName = `inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${toneStyles[tone]}`
  const chipContent = (
    <>
      <span className={`h-1.5 w-1.5 rounded-full ${toneDot[tone]}`} />
      {stage.label}
    </>
  )

  return dealUrl ? (
    <a
      className={`${chipClassName} hover:brightness-95`}
      href={dealUrl}
      rel="noopener noreferrer"
      target="_blank"
      title="Open deal in HubSpot"
    >
      {chipContent}
      <FontAwesomeIcon
        className="h-2.5 w-2.5 opacity-60"
        icon={faArrowUpRightFromSquare}
      />
    </a>
  ) : (
    <span className={chipClassName}>{chipContent}</span>
  )
}

const CaseTable = ({ cases }) => {
  const sorted = [...cases].sort(
    (a, b) => (getUnifiedStage(b) ?? -1) - (getUnifiedStage(a) ?? -1)
  )

  return (
    <table className="w-full border-separate border-spacing-0">
      <thead>
        <tr>
          {[
            "Accession",
            "Modality",
            "Referring Doctor",
            "Stage",
            "Tracking starting date",
          ].map((h) => (
            <th
              key={h}
              className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-400"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sorted.map((c) => (
          <tr key={c.OnboardingStudyTrackingId}>
            <td className="border-t border-gray-100 px-3 py-2.5 font-mono text-sm text-gray-700">
              {c.AccessionNo || "—"}
            </td>
            <td className="border-t border-gray-100 px-3 py-2.5">
              {c.Modality ? (
                <span className="inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[11.5px] font-semibold text-gray-700">
                  {c.Modality}
                </span>
              ) : (
                <span className="text-sm text-gray-400">—</span>
              )}
            </td>
            <td className="border-t border-gray-100 px-3 py-2.5 text-sm text-gray-700">
              {c.ReferringPhysician || "—"}
            </td>
            <td className="border-t border-gray-100 px-3 py-2.5 align-top">
              <StageChip study={c} />
            </td>
            <td className="border-t border-gray-100 px-3 py-2.5 text-sm text-gray-500">
              {formatISODate(c.CreatedAt, "MMM d, yyyy") || "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const CentersList = ({
  data,
  total,
  isLoading,
  isError,
  error,
  pagination,
  pageCount,
  onPaginationChange,
  studyLimit,
  hasFilters,
  onClearFilters,
}) => {
  const [expandedCenterId, setExpandedCenterId] = useState(null)

  const table = useReactTable({
    columns: [],
    data,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange,
    pageCount,
    state: { pagination },
  })

  const toggleCenter = (id) =>
    setExpandedCenterId((prev) => (prev === id ? null : id))

  if (isError) {
    return (
      <div className="rounded-xl border border-gray-200 p-4">
        <ApiError axiosError={error} />
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex justify-center rounded-xl border border-gray-200 py-16">
        <Loader />
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 py-16 text-center text-sm text-gray-500">
        {hasFilters
          ? "No centers match the current filters."
          : "No centers yet."}
        {hasFilters && (
          <button
            className="mt-3 block w-full text-sm font-semibold text-secondary_dark hover:underline"
            type="button"
            onClick={onClearFilters}
          >
            Clear filters
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-gray-200">
      <div className="divide-y divide-gray-100">
        {data.map((center) => {
          const isExpanded = expandedCenterId === center.CustomerOnboardingId
          const cases = center.Cases || []
          const caseCount = center.CaseCount ?? cases.length

          return (
            <Fragment key={center.CustomerOnboardingId}>
              <button
                aria-expanded={isExpanded}
                className="grid w-full grid-cols-[1.7fr_.9fr_1.5fr_140px_20px] items-center gap-4 px-4 py-3.5 text-left hover:bg-gray-50"
                type="button"
                onClick={() => toggleCenter(center.CustomerOnboardingId)}
              >
                <div className="min-w-0">
                  <div className="truncate text-[14.5px] font-semibold text-gray-900">
                    {center.PatientIssuerName || "—"}
                  </div>
                  <div className="mt-0.5 text-xs text-gray-400">
                    Tracking since{" "}
                    {formatISODate(center.OnboardedAt, "MMM d, yyyy")}
                  </div>
                </div>
                <div className="text-sm text-gray-700">
                  <b className="text-gray-900">{caseCount}</b>{" "}
                  {caseCount === 1 ? "study" : "studies"}
                </div>
                <MiniStageBar cases={cases} />
                <div className="justify-self-start">
                  <CenterStatusPill center={center} studyLimit={studyLimit} />
                </div>
                <FontAwesomeIcon
                  className={`h-3.5 w-3.5 justify-self-end text-gray-400 transition-transform ${
                    isExpanded ? "rotate-180" : ""
                  }`}
                  icon={faChevronDown}
                />
              </button>

              {isExpanded && (
                <div className="overflow-x-auto bg-gray-50 px-4 pb-4 pt-1">
                  <CaseTable cases={cases} />
                </div>
              )}
            </Fragment>
          )
        })}
      </div>

      <div className="flex items-center justify-between border-t border-gray-100 px-4">
        <span className="py-3 text-xs text-gray-500">
          Showing <span className="font-semibold">{data.length}</span> of{" "}
          <span className="font-semibold">{total}</span> centers
        </span>
        <Pagination tableLib={table} />
      </div>
    </div>
  )
}

export default CentersList
